import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useFetcher } from 'react-router';
import { useAtom } from 'jotai';
import {
    Check,
    ChevronLeft,
    ChevronRight,
    Loader2,
    Plus,
    Search,
    X,
} from 'lucide-react';
import { useTracker } from '@archipro-website/tracker';
import { cn } from '@rocco/utils/cn';
import { Button } from '@rocco/components/button';
import { Input } from '@rocco/components/input';
import { Spinner } from '@rocco/components/spinner';
import type { DesignBoardViewModel } from '@rocco/ui/design-board/view-models/design-board';
import { useUserLoggedIn } from '@modules/user';
import { usePersonalWorkspace } from '~/modules/user/hook/use-personal-workspace';
import { BOARD_TERMS } from '~/config/mood-board-terminology';
import { useBoardsService } from '~/modules/moodboards/hooks/use-boards-service';
import {
    buildSections,
    buildThumbnail,
} from '~/modules/moodboards/utils/build-sections';
import { resolveThumbnailTreatment } from '~/modules/moodboards/utils/thumbnail-treatment';
import {
    fetchPinMembershipBoardIds,
    getSavedBoardIdsForPin,
    hasMembershipCacheForPin,
    makePinKey,
    pinMembershipCacheAtom,
    resolveTrackedPinId,
} from '~/modules/moodboards/state/design-board-save-state';
import type { FollowSelectorItem } from '~/modules/moodboards/types';
import type { PinItemType } from '~/modules/moodboards/type';
import { CREATE_BUILD_HUB_INSPIRATION_API_ROUTE } from '~/routes/remix-api.app.build-hub-inspiration-create';
import type { CreateOrUpdateInspirationMutation } from 'generated/graphql';

/**
 * Below this many boards the list is scannable at a glance and the frame shows
 * no search field; from here up (or while paginated) search earns its place.
 */
const SEARCH_VISIBLE_MIN_BOARDS = 6;

/** Square board thumbnail matching the choose-board frame. */
const TILE_CLASS =
    'flex h-[3.1875rem] w-[3.125rem] shrink-0 items-center justify-center overflow-hidden rounded-[0.625rem]';

/**
 * Row/footer hover pill: full column width, bleeding 0.5rem past both edges so
 * the fill reads as a highlight rather than a boxed button.
 */
const ROW_CLASS =
    '-mx-2 flex w-[calc(100%+1rem)] items-center gap-[0.6875rem] rounded-[0.75rem] px-2 py-1 text-left transition-colors focus:bg-black/5 focus:outline-hidden md:hover:bg-black/5';

/** The pin the gate is saving, for marking boards that already hold it. */
export interface SaveGatePinTarget {
    id: number;
    pinType?: string;
    libraryItemId?: number;
}

export interface SaveGateBoardPickerProps {
    /**
     * A board was picked or created; the gate resumes the save into it. For a
     * created board `isNew` is true so the resume can treat it as fresh.
     */
    onBoardChosen: (
        board: FollowSelectorItem,
        options: { isNew: boolean },
    ) => void;
    /**
     * The board the resume is currently saving into. Picking is disabled while
     * set, and that row shows a spinner in place of its chevron.
     */
    processingBoardId?: number | null;
    /**
     * The account was created moments ago in this gate, so the greeting drops
     * the "back" — there is nothing to be back from.
     */
    isNewAccount?: boolean;
    /**
     * The pin being saved. When set, boards that already hold it show a Saved
     * tick; picking one anyway is harmless — the server skips existing pins.
     */
    pin?: SaveGatePinTarget;
}

interface BoardRowProps {
    item: FollowSelectorItem;
    onSelect: (item: FollowSelectorItem) => void;
    disabled: boolean;
    isProcessing: boolean;
}

/** One board: thumbnail, title, owning project, chevron. */
const BoardRow = ({
    item,
    onSelect,
    disabled,
    isProcessing,
}: BoardRowProps) => {
    const thumbnail = item.thumbnail;
    const treatment = resolveThumbnailTreatment(thumbnail);

    return (
        <button
            type="button"
            onClick={() => onSelect(item)}
            disabled={disabled}
            className={cn(
                ROW_CLASS,
                isProcessing && 'cursor-wait',
                disabled && !isProcessing && 'opacity-60',
            )}
            aria-label={`Save to ${item.title}${
                item.projectName && !item.isPersonal
                    ? ` in ${item.projectName}`
                    : ''
            }${item.isSaved ? ' (already saved)' : ''}`}
        >
            <span
                className={cn(
                    TILE_CLASS,
                    !treatment.backgroundColor && 'bg-[#f3f3f5]',
                )}
                style={{ backgroundColor: treatment.backgroundColor }}
            >
                {thumbnail?.src && (
                    <img
                        src={thumbnail.src}
                        alt=""
                        className={cn(
                            'h-full w-full',
                            treatment.contain
                                ? 'object-contain p-1'
                                : 'object-cover',
                        )}
                        style={{
                            mixBlendMode: treatment.multiply
                                ? 'multiply'
                                : undefined,
                        }}
                    />
                )}
            </span>
            <span className="min-w-0 flex-1">
                <span className="block truncate text-[1.1875rem] leading-tight font-medium tracking-[-0.02em] text-[#181818]">
                    {item.title}
                </span>
                {item.projectName && !item.isPersonal && (
                    <span className="block truncate text-[0.875rem] leading-tight tracking-[-0.02em] text-[#181818]">
                        {item.projectName}
                    </span>
                )}
            </span>
            {isProcessing ? (
                <Loader2 className="size-4 shrink-0 animate-spin text-[#181818]" />
            ) : item.isSaved ? (
                <span className="flex shrink-0 items-center gap-1 text-[0.875rem] font-medium tracking-[-0.02em] text-[#818181]">
                    <Check className="size-3.5" strokeWidth={2.5} />
                    Saved
                </span>
            ) : (
                <ChevronRight className="size-3.5 shrink-0 text-[#181818]" />
            )}
        </button>
    );
};

/**
 * The `[choose board]` step of the save gate: the auth segment is behind the
 * reader and the save needs a destination, so their design boards are offered
 * with search, pagination, and an inline create form. This is the gate-pane
 * counterpart of the FollowSelector popover — same data service, presentation
 * per the choose-board frame.
 */
export const SaveGateBoardPicker = ({
    onBoardChosen,
    processingBoardId = null,
    isNewAccount = false,
    pin,
}: SaveGateBoardPickerProps) => {
    const tracker = useTracker();
    const { user } = useUserLoggedIn();
    const firstName =
        'FirstName' in user ? user.FirstName || undefined : undefined;

    const boardsService = useBoardsService();
    const {
        boards,
        searchResults,
        searchQuery,
        search,
        isSearching,
        isLoading,
        initialFetch,
        hasNextPage,
        loadMore,
        isLoadingMore,
        prependBoard,
    } = boardsService;

    const scrollAreaRef = useRef<HTMLDivElement>(null);
    const loadMoreSentinelRef = useRef<HTMLDivElement>(null);

    const [isAdding, setIsAdding] = useState(false);
    const [boardName, setBoardName] = useState('');
    const [createError, setCreateError] = useState<string | null>(null);
    const createInputRef = useRef<HTMLInputElement>(null);
    const lastProcessedBoardId = useRef<number | null>(null);
    const createFetcher = useFetcher<CreateOrUpdateInspirationMutation>();
    const personalWorkspace = usePersonalWorkspace();
    const isCreating = createFetcher.state !== 'idle';

    const isResuming = processingBoardId !== null;

    // The picker mounts straight after auth, which can beat the app-root
    // boards prefetch. Ask once; the shared cache makes a duplicate harmless.
    useEffect(() => {
        if (boards.length === 0) {
            initialFetch();
        }
        // Once, on mount: results landing later must not retrigger this.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Mark boards that already hold this pin — the reader may have saved it in
    // an earlier session before being gated on this one.
    const [membershipCache, setMembershipCache] = useAtom(
        pinMembershipCacheAtom,
    );
    // Same default the FollowSelector context applies to untyped pins.
    const pinType = (pin?.pinType as PinItemType | undefined) ?? 'ProductPin';
    const trackedPinId = pin
        ? resolveTrackedPinId(pin.id, pin.libraryItemId, pinType)
        : undefined;

    useEffect(() => {
        if (
            !trackedPinId ||
            hasMembershipCacheForPin(membershipCache, trackedPinId, pinType)
        ) {
            return;
        }
        let cancelled = false;
        fetchPinMembershipBoardIds(trackedPinId, pinType)
            .then((boardIds) => {
                if (cancelled) return;
                setMembershipCache((prev) => {
                    const next = new Map(prev);
                    next.set(
                        makePinKey(trackedPinId, pinType),
                        new Set(boardIds),
                    );
                    return next;
                });
            })
            .catch(() => {
                // The saved marks are a nicety; the list still works without.
            });
        return () => {
            cancelled = true;
        };
    }, [trackedPinId, pinType, membershipCache, setMembershipCache]);

    const isSavedToBoard = useCallback(
        (boardId: number): boolean =>
            trackedPinId !== undefined &&
            getSavedBoardIdsForPin(
                membershipCache,
                trackedPinId,
                pinType,
            ).includes(boardId),
        [membershipCache, trackedPinId, pinType],
    );

    const items = useMemo(
        () =>
            buildSections(
                boards,
                searchResults,
                searchQuery,
                isSavedToBoard,
            ).flatMap((section) => section.items),
        [boards, searchResults, searchQuery, isSavedToBoard],
    );

    const searchVisible =
        searchQuery.trim().length > 0 ||
        hasNextPage ||
        boards.length >= SEARCH_VISIBLE_MIN_BOARDS;

    // Infinite scroll: fetch the next page as the sentinel nears the viewport.
    useEffect(() => {
        const sentinel = loadMoreSentinelRef.current;
        const scrollArea = scrollAreaRef.current;
        if (!sentinel || !scrollArea || !hasNextPage || isLoadingMore) {
            return;
        }

        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0]?.isIntersecting) {
                    loadMore();
                }
            },
            { root: scrollArea, rootMargin: '100px', threshold: 0.1 },
        );
        observer.observe(sentinel);
        return () => observer.disconnect();
    }, [hasNextPage, isLoadingMore, loadMore]);

    useEffect(() => {
        if (isAdding) {
            createInputRef.current?.focus();
        }
    }, [isAdding]);

    // Resolve the create-board submission: choose the fresh board so the save
    // lands in it without another tap.
    useEffect(() => {
        if (!createFetcher.data || createFetcher.state !== 'idle') {
            return;
        }
        const result = createFetcher.data.projectInspirationGroupUpsert;
        if (result.__typename !== 'InspirationGroup') {
            setCreateError(
                'Message' in result ? result.Message : 'Failed to create board',
            );
            return;
        }

        const boardId = parseInt(result.ID, 10);
        if (lastProcessedBoardId.current === boardId) {
            return;
        }
        lastProcessedBoardId.current = boardId;

        const board: DesignBoardViewModel = {
            id: boardId,
            title: result.Title,
            urlSegment: result.URLSegment,
            isNew: true,
            pins: [],
        };
        setIsAdding(false);
        setBoardName('');
        setCreateError(null);
        prependBoard(board);
        onBoardChosen(
            {
                id: board.id,
                title: board.title,
                thumbnailUrls: [],
                thumbnail: buildThumbnail(board),
                entityType: 'moodboard',
                urlSegment: board.urlSegment,
                // Created in the reader's personal workspace.
                isPersonal: true,
            },
            { isNew: true },
        );
    }, [createFetcher.data, createFetcher.state, prependBoard, onBoardChosen]);

    const handleSelect = useCallback(
        (item: FollowSelectorItem) => {
            if (isResuming) return;
            onBoardChosen(item, { isNew: false });
        },
        [isResuming, onBoardChosen],
    );

    const handleCreateSubmit = (event: React.FormEvent) => {
        event.preventDefault();
        if (!boardName.trim()) {
            setCreateError('Please enter a board name');
            return;
        }
        if (!personalWorkspace) {
            setCreateError('Personal workspace not found');
            return;
        }

        setCreateError(null);
        void tracker.log('DesignBoardCreateSubmit', {
            url: new URL(window.location.href),
        });

        const formData = new FormData();
        formData.append('id', personalWorkspace.ID.toString());
        formData.append('title', boardName.trim());
        formData.append('description', '');
        formData.append('accessMode', 'restricted');
        formData.append('accessRole', 'Editor');
        createFetcher.submit(formData, {
            method: 'post',
            action: CREATE_BUILD_HUB_INSPIRATION_API_ROUTE,
        });
    };

    const handleCreateCancel = () => {
        setIsAdding(false);
        setBoardName('');
        setCreateError(null);
    };

    return (
        <div className="mx-auto flex min-h-0 w-full max-w-90.75 flex-1 flex-col">
            <h2 className="mt-3 shrink-0 text-[2.0625rem] leading-[2.125rem] font-medium tracking-[-0.02em] text-[#181818]">
                {isNewAccount
                    ? firstName
                        ? `Welcome, ${firstName}`
                        : 'Welcome'
                    : firstName
                      ? `Welcome back, ${firstName}`
                      : 'Welcome back'}
            </h2>

            <p className="mt-9 shrink-0 text-[0.875rem] tracking-[-0.02em] text-[#818181]">
                {BOARD_TERMS.saveTo}:
            </p>

            {searchVisible && (
                <div className="relative mt-3 flex shrink-0 items-center">
                    <Search className="pointer-events-none absolute left-3.5 size-4 text-gray-400" />
                    <input
                        type="text"
                        value={searchQuery}
                        onChange={(event) => search(event.target.value)}
                        placeholder={BOARD_TERMS.searchBoards}
                        aria-label={BOARD_TERMS.searchBoards}
                        className="h-10 w-full rounded-full border border-gray-300 bg-white pr-10 pl-10 text-[16px] tracking-[-0.02em] text-gray-900 placeholder:text-gray-400 focus:border-gray-400 focus:ring-0 focus:outline-hidden md:text-sm"
                    />
                    {isSearching ? (
                        <Loader2 className="absolute right-3.5 size-4 animate-spin text-gray-400" />
                    ) : (
                        searchQuery.length > 0 && (
                            <button
                                type="button"
                                onClick={() => search('')}
                                aria-label="Clear search"
                                className="absolute right-3 rounded-full p-0.5 text-gray-400 transition-colors hover:text-gray-900"
                            >
                                <X className="size-4" />
                            </button>
                        )
                    )}
                </div>
            )}

            <div
                ref={scrollAreaRef}
                className="mt-4 scrollbar-hide min-h-0 flex-1 overflow-y-auto overscroll-contain"
            >
                {isLoading ? (
                    <div className="flex items-center justify-center py-8">
                        <Spinner />
                    </div>
                ) : items.length > 0 ? (
                    <div className="flex flex-col gap-2">
                        {items.map((item) => (
                            <BoardRow
                                key={item.id}
                                item={item}
                                onSelect={handleSelect}
                                disabled={isResuming}
                                isProcessing={processingBoardId === item.id}
                            />
                        ))}
                        {isLoadingMore && (
                            <div className="flex items-center justify-center py-3">
                                <Loader2 className="size-4 animate-spin text-[#818181]" />
                            </div>
                        )}
                        {hasNextPage && !isLoadingMore && (
                            <div
                                ref={loadMoreSentinelRef}
                                className="h-1"
                                aria-hidden="true"
                            />
                        )}
                    </div>
                ) : (
                    <p className="py-6 text-[0.9375rem] tracking-[-0.02em] text-[#828282]">
                        {searchQuery.trim()
                            ? 'No Design Boards match your search.'
                            : 'You don’t have a Design Board yet — create your first one below.'}
                    </p>
                )}
            </div>

            <div className="mt-4 shrink-0 border-t border-black/10 pt-4">
                {isAdding ? (
                    <form onSubmit={handleCreateSubmit}>
                        <div className="flex items-center gap-2">
                            <button
                                type="button"
                                onClick={handleCreateCancel}
                                disabled={isCreating}
                                aria-label="Back"
                                className={cn(TILE_CLASS, 'bg-[#f3f3f5]')}
                            >
                                <ChevronLeft className="size-4 text-[#181818]" />
                            </button>
                            <Input
                                ref={createInputRef}
                                type="text"
                                value={boardName}
                                onChange={(event) =>
                                    setBoardName(event.target.value)
                                }
                                placeholder={BOARD_TERMS.nameYourBoard}
                                disabled={isCreating}
                                color="transparent"
                                className="h-[3.1875rem] flex-1 rounded-[0.625rem] bg-[#f3f3f5] px-4 text-[16px] tracking-[-0.02em] md:text-sm"
                            />
                            <Button
                                type="submit"
                                disabled={isCreating || !boardName.trim()}
                                color="dark"
                                size="narrow"
                                isLoading={isCreating}
                                className="h-[3.1875rem] min-w-[4.5rem] rounded-[0.625rem] font-medium"
                            >
                                Create
                            </Button>
                        </div>
                        {createError && (
                            <p className="mt-2 text-sm text-red-600">
                                {createError}
                            </p>
                        )}
                    </form>
                ) : (
                    <button
                        type="button"
                        onClick={() => setIsAdding(true)}
                        disabled={isResuming}
                        className={cn(ROW_CLASS, isResuming && 'opacity-60')}
                    >
                        <span className={cn(TILE_CLASS, 'bg-[#f3f3f5]')}>
                            <Plus className="size-3.5 text-[#181818]" />
                        </span>
                        <span className="text-[1.1875rem] font-medium tracking-[-0.02em] text-[#181818]">
                            Add New Design Board
                        </span>
                    </button>
                )}
            </div>
        </div>
    );
};
